import { ROUTE_NAMES } from '@/constant/routes'

// AHA-47221: the add-in's editor entrance.
//
// /ppt-addin/edit-slides/:id renders exactly what /presentation/:id renders — same component,
// same presentation — and differs only in what the pane is FOR: it is the editor, opened with
// the editing tools and the slide picker both live, rather than the presenting canvas.
//
// It exists as a route rather than as a store flag because the state has to survive a task-pane
// reload. PowerPoint reloads the pane on its own schedule, and a store flag does not come back
// from that — the URL does.
//
// It does not unbind the frame: the user is choosing what to do NEXT with the presentation they
// are presenting from, not leaving it. That is the whole difference from the back arrow
// (BackToEditorButton), which clears dataSlidePPT on the way out.
export const PPT_ADDIN_EDITOR_ROUTES = [
  ROUTE_NAMES.PPT_ADDIN_EDIT_SLIDES,
]

// The path the route above resolves to, minus its :id.
//
// Exported for the one caller that cannot ask the router: services/office runs on the cold-load
// path, where it reads window.location.href directly because the router has not necessarily
// settled on the booted route yet (see coldLoadNavigated).
export const PPT_ADDIN_EDITOR_PATH = '/ppt-addin/edit-slides/'

// "This WINDOW is showing the add-in's editor entrance", asked of the raw URL rather than of the
// router, for that same cold-load caller.
export const isOnPptAddinEditorUrl = () => {
  return window.location.href.indexOf(PPT_ADDIN_EDITOR_PATH) !== -1
}

// "This route is the add-in's editor entrance." Used where the app tests for the presentation
// editor by route name and would otherwise treat it as somewhere else entirely.
export const isPptAddinEditorRoute = (routeName) => {
  return PPT_ADDIN_EDITOR_ROUTES.includes(routeName)
}

// AHA-47221: "this window opened straight onto a presentation that had just been created."
//
// The embed list's "New presentation" button makes the presentation and opens the add-in's editor
// entrance on it in an Office dialog, carrying `new=true` — the same param the dashboard's own
// create sends, which the editor reads as "made a moment ago" (PresentationEditor).
//
// Asked of the ROUTE rather than of state.isNewPresentation, which PresentationEditor clears as
// soon as the user picks a slide type: what this answers is how the WINDOW was opened, and that
// does not change while it is open. It survives the address bar losing the param on the way in
// too — that strip is a replaceState, which does not re-run the router.
export const NEW_PRESENTATION_QUERY = 'new'

export const isPptAddinNewPresentationRoute = (route) => {
  if (!route || !isPptAddinEditorRoute(route.name)) return false
  return String((route.query || {})[NEW_PRESENTATION_QUERY]) === 'true'
}

// AHA-47221: "the user came here to ADD slides", carried in the URL.
//
// The editor's sidebar opens with the first selectable slide already ticked (see PptEditorMixin).
// That is a helpful starting position for someone who arrived to pick slides for the deck, and
// the wrong one for someone who arrived to edit: a tick they did not make, on a presentation
// they only meant to look at, which the Add bar then offers to put into PowerPoint.
//
// In the URL rather than in the store for the same reason the edit-mode route is: the editor can
// be opened in an Office DIALOG, which is a separate browser context with its own store, so the
// URL is the only thing the two ends share — and it survives PowerPoint reloading the task pane.
export const PPT_ADD_SLIDES_QUERY = 'addSlide'

// The query object to push, so no caller has to spell the value out. Empty when the intent is
// not "add slides": an absent param and an explicit false one mean the same thing here, and
// leaving it out keeps the URL saying only what it has to.
export const pptAddSlidesQuery = (addSlides) => {
  return addSlides ? { [PPT_ADD_SLIDES_QUERY]: 'true' } : {}
}

// Read back off a route's query. Compared as a STRING because that is what a query value is on
// the way in and on the way out — the router hands back text, never a boolean.
export const isPptAddSlidesIntent = (query) => {
  return String((query || {})[PPT_ADD_SLIDES_QUERY]) === 'true'
}
